import { useCallback, useEffect, useRef, useState } from "react";
import {
  submitPropertySummary,
  getPropertySummaryJob,
} from "../services/propertyApiService";
import {
  buildPropertyImageUrl,
  toSummaryImageIndex,
} from "../services/adapters/propertyAssessment";

const POLL_INTERVAL_MS = 5000;

/**
 * Shared job-lifecycle logic for the AI property summary, used by both the
 * legacy and redesigned Property Case Details pages — only the rendering
 * differs between them, matching how statusMapping.js's Clearline helpers
 * are shared without the two designs sharing JSX.
 *
 * Talks directly to the Property Insights service (CLIENT_GUIDE.md) via
 * propertyApiService.js's submitPropertySummary/getPropertySummaryJob — an
 * unauthenticated service with no notion of "this app's cases" at all, so
 * unlike the old GET /get_summary wrapper this replaces, there is no ref_num
 * to ask it for a status update with. generate() now takes the case's own
 * photos, fetches each one as bytes, and submits them itself before it has
 * anything to poll.
 */
export function usePropertyAiSummary() {
  const [status, setStatus] = useState("idle"); // idle | processing | completed | failed
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  // The exact photos submitted, 1-indexed in submission order — this is what
  // resolveSummaryEvidenceUrls/ImageInventoryStrip read evidence_images
  // against, since the service doesn't echo an images list back the way the
  // old wrapper did; the submission order itself is the only record of
  // which index means which photo, and this app is what decided it.
  const [images, setImages] = useState([]);
  // finished_at straight off the job, for the report header's "Generated
  // <time>" line — matches what the old portal's own TriNetra summary page
  // showed for the equivalent field.
  const [completedAt, setCompletedAt] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  // Identifies the current generate() call, so a case switch — or a second
  // click of Regenerate before the first finishes — can't have a stale
  // fetch/poll loop overwrite newer state once it finally resolves.
  const requestIdRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // One GET /v1/jobs/{id} call. Returns true once the job has settled
  // (completed, failed, or errored) so the caller knows whether to keep
  // polling.
  const check = useCallback(async (jobId, requestId) => {
    try {
      const job = await getPropertySummaryJob(jobId);
      if (requestIdRef.current !== requestId) return true; // superseded
      setProgress(job?.progress || null);
      if (job?.status === "completed") {
        setStatus("completed");
        setResult(job?.result || null);
        setCompletedAt(job?.finished_at || null);
        return true;
      }
      if (job?.status === "failed") {
        setStatus("failed");
        setError(job?.error?.message || "Analysis failed.");
        return true;
      }
      setStatus("processing");
      return false;
    } catch (err) {
      if (requestIdRef.current !== requestId) return true;
      setStatus("failed");
      // A job's result is only kept for an hour after it completes
      // (CLIENT_GUIDE.md) — checked too late, this looks identical to an
      // id that never existed, so "expired" is the more useful read of a 404
      // here than a raw unknown-id message.
      setError(
        err.response?.status === 404
          ? "This analysis has expired — results are only kept for an hour. Try again."
          : err.message || "Failed to check analysis status.",
      );
      return true;
    }
  }, []);

  /**
   * `photos`: the case's own images (e.g. overview.images / property_data) —
   * each needs a `picture` path resolvable via buildPropertyImageUrl.
   * `mediaBaseUrl`: resolves those paths to fetchable URLs.
   * `notes`/`country`: optional context passed straight to the service.
   */
  const generate = useCallback(
    async ({ photos, mediaBaseUrl, notes, country } = {}) => {
      stopPolling();
      const requestId = ++requestIdRef.current;
      setResult(null);
      setCompletedAt(null);
      setError(null);
      setProgress(null);

      const indexed = toSummaryImageIndex(photos);
      if (indexed.length === 0) {
        setStatus("failed");
        setError("No photos available to analyze.");
        setImages([]);
        return;
      }

      // Set up front, independent of whether submission itself succeeds —
      // evidence/inventory rendering only matters once `result` exists, but
      // keeping this in sync with what was actually attempted avoids a stale
      // list from a previous case lingering through a failure.
      setImages(indexed);
      setStatus("processing");

      let files;
      try {
        files = await Promise.all(
          indexed.map(async ({ picture }, i) => {
            const url = buildPropertyImageUrl(mediaBaseUrl, picture);
            const res = await fetch(url);
            if (!res.ok) {
              throw new Error(`Could not fetch photo ${i + 1} (HTTP ${res.status})`);
            }
            const blob = await res.blob();
            return new File([blob], `photo${i + 1}.jpg`, {
              type: blob.type || "image/jpeg",
            });
          }),
        );
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setStatus("failed");
        setError(err.message || "Failed to prepare photos for analysis.");
        return;
      }
      if (requestIdRef.current !== requestId) return;

      let job;
      try {
        job = await submitPropertySummary({ files, notes, country });
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setStatus("failed");
        setError(
          err.response?.data?.detail ||
            err.message ||
            "Failed to submit photos for analysis.",
        );
        return;
      }
      if (requestIdRef.current !== requestId) return;

      const jobId = job?.job_id;
      if (!jobId) {
        setStatus("failed");
        setError("No job id returned for this analysis.");
        return;
      }

      const settled = await check(jobId, requestId);
      if (settled || requestIdRef.current !== requestId) return;

      pollRef.current = setInterval(async () => {
        const done = await check(jobId, requestId);
        if (done) stopPolling();
      }, POLL_INTERVAL_MS);
    },
    [check, stopPolling],
  );

  const reset = useCallback(() => {
    stopPolling();
    requestIdRef.current += 1; // invalidates anything still in flight
    setStatus("idle");
    setProgress(null);
    setResult(null);
    setImages([]);
    setCompletedAt(null);
    setError(null);
  }, [stopPolling]);

  return {
    status,
    progress,
    result,
    images,
    completedAt,
    error,
    generate,
    reset,
  };
}
